import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Rider.css';

const DELIVERY_FEE = 120;

// ─── Status Config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending:              { color: '#D97706', bg: '#FEF3C7', icon: '⏳' },
  accepted:             { color: '#2d6a4f', bg: '#d8f3dc', icon: '👍' },
  assigned:             { color: '#6D28D9', bg: '#EDE9FE', icon: '🏍️' },
  confirmed:            { color: '#059669', bg: '#D1FAE5', icon: '✅' },
  processing:           { color: '#EA580C', bg: '#FFEDD5', icon: '⚙️' },
  'out-for-delivery':   { color: '#0891B2', bg: '#CFFAFE', icon: '🚚' },
  delivered:            { color: '#16A34A', bg: '#DCFCE7', icon: '📦' },
  cancelled:            { color: '#DC2626', bg: '#FEE2E2', icon: '❌' },
};
const getStatusCfg = (s) =>
  STATUS_CONFIG[String(s || '').toLowerCase()] || { color: '#6B7280', bg: '#F3F4F6', icon: '❓' };

// ─── Mini Bar Chart ────────────────────────────────────────────────────────────
const MiniBarChart = ({ delivered, active, cancelled, pending }) => {
  const total = delivered + active + cancelled + pending || 1;
  const bars = [
    { label: 'Done',    value: delivered, color: '#16A34A' },
    { label: 'Active',  value: active,    color: '#2d6a4f' },
    { label: 'Cancel',  value: cancelled, color: '#DC2626' },
    { label: 'Pending', value: pending,   color: '#D97706' },
  ];
  return (
    <div className="mini-bar-chart">
      {bars.map((b) => {
        const h = Math.max(4, (b.value / total) * 80);
        return (
          <div key={b.label} className="mini-bar-col">
            <span className="mini-bar-num">{b.value}</span>
            <div className="mini-bar-fill" style={{ height: h, backgroundColor: b.color }} />
            <span className="mini-bar-label">{b.label}</span>
          </div>
        );
      })}
    </div>
  );
};

export default function RiderDashboard() {
  const { api, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [orders,     setOrders]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [dateFilter, setDateFilter] = useState('month');

  const riderId = String(user?.id || '');

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      setError('');
      const response = await api.get('/orders');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.orders || response.data?.data || []);
      setOrders(data);
    } catch (err) {
      setError('Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => { setRefreshing(true); fetchOrders(); };

  const getRiderId    = (o) => String(o?.rider_id || o?.riderId || o?.assigned_rider || o?.rider?.id || '');
  const getCreatedDate= (o) => { const d = o?.created_at || o?.createdAt; const dt = d ? new Date(d) : null; return dt && !isNaN(dt) ? dt : null; };
  const getDeliveredDate=(o) => { const d = o?.delivered_at || o?.deliveredAt; const dt = d ? new Date(d) : null; return dt && !isNaN(dt) ? dt : null; };
  const getFilterDate = (o) => {
    const st = String(o?.status || '').toLowerCase();
    return st === 'delivered' ? (getDeliveredDate(o) || getCreatedDate(o)) : getCreatedDate(o);
  };

  const isSameDay  = (d1, d2) => d1.getFullYear()===d2.getFullYear() && d1.getMonth()===d2.getMonth() && d1.getDate()===d2.getDate();
  const isThisWeek = (dt) => {
    const now = new Date();
    const sw = new Date(now); sw.setDate(now.getDate()-now.getDay()); sw.setHours(0,0,0,0);
    const ew = new Date(sw);  ew.setDate(sw.getDate()+6); ew.setHours(23,59,59,999);
    return dt >= sw && dt <= ew;
  };
  const isThisMonth = (dt) => { const n=new Date(); return dt.getMonth()===n.getMonth() && dt.getFullYear()===n.getFullYear(); };
  const isLastMonth = (dt) => { const n=new Date(); const l=new Date(n.getFullYear(),n.getMonth()-1,1); return dt.getMonth()===l.getMonth() && dt.getFullYear()===l.getFullYear(); };

  const myOrdersAll = useMemo(() =>
    orders.filter((o) => {
      const oRid = getRiderId(o);
      const st   = String(o?.status||'').toLowerCase();
      return (oRid && oRid === riderId) || (st === 'pending' && !oRid);
    }), [orders, riderId]);

  const filteredByDate = useMemo(() => {
    if (dateFilter === 'all') return myOrdersAll;
    const now = new Date();
    return myOrdersAll.filter((o) => {
      const dt = getFilterDate(o);
      if (!dt) return false;
      if (dateFilter === 'today')     return isSameDay(dt, now);
      if (dateFilter === 'week')      return isThisWeek(dt);
      if (dateFilter === 'month')     return isThisMonth(dt);
      if (dateFilter === 'lastMonth') return isLastMonth(dt);
      return true;
    });
  }, [myOrdersAll, dateFilter]);

  const dashboardOrders = useMemo(() =>
    [...filteredByDate].sort((a,b) => (getFilterDate(b)||new Date(0)).getTime() - (getFilterDate(a)||new Date(0)).getTime()),
    [filteredByDate]);

  const stats = useMemo(() => {
    const mine      = filteredByDate.filter((o) => getRiderId(o) === riderId);
    const active    = mine.filter((o) => ['accepted','assigned','confirmed','processing','out-for-delivery'].includes(String(o?.status||'').toLowerCase())).length;
    const delivered = mine.filter((o) => String(o?.status||'').toLowerCase() === 'delivered').length;
    const cancelled = mine.filter((o) => String(o?.status||'').toLowerCase() === 'cancelled').length;
    const pending   = filteredByDate.filter((o) => String(o?.status||'').toLowerCase() === 'pending' && !getRiderId(o)).length;
    return { total: filteredByDate.length, active, delivered, cancelled, pending, earnings: delivered * DELIVERY_FEE, potential: (delivered + active) * DELIVERY_FEE };
  }, [filteredByDate, riderId]);

  if (loading) return <LoadingSpinner message="Loading dashboard..." />;

  const DATE_TABS = [
    { value: 'today',     label: 'Today',      icon: '☀️' },
    { value: 'week',      label: 'This Week',  icon: '📅' },
    { value: 'month',     label: 'This Month', icon: '🗓️' },
    { value: 'lastMonth', label: 'Last Month', icon: '⏮️' },
    { value: 'all',       label: 'All Time',   icon: '♾️' },
  ];

  const earningsPct = stats.potential > 0 ? Math.min((stats.earnings / stats.potential) * 100, 100) : 0;

  return (
    <div className="rider-dashboard">
      <div className="rd-container">

        {/* ══ HERO HEADER ══════════════════════════════════════ */}
        <div className="rd-hero">
          <div className="rd-hero-bubble rd-hero-bubble-1" />
          <div className="rd-hero-bubble rd-hero-bubble-2" />

          <div className="rd-hero-top">
            <div className="rd-hero-left">
              <div className="rd-avatar">
                {user?.name?.[0]?.toUpperCase() || '🏍'}
              </div>
              <div className="rd-hero-info">
                <span className="rd-hero-greet">Welcome back 👋</span>
                <h1 className="rd-hero-name">{user?.name || 'Rider'}</h1>
                <div className="rd-online-badge">
                  <span className="rd-online-dot" />
                  <span>Active Rider</span>
                </div>
              </div>
            </div>
            <div className="rd-hero-actions">
              <button className="rd-refresh-btn" onClick={handleRefresh} disabled={refreshing}>
                <span className={refreshing ? 'spin' : ''}>🔄</span>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Quick stats strip */}
          <div className="rd-hero-stats">
            <div className="rd-hero-stat">
              <span className="rd-hero-stat-num">{stats.delivered}</span>
              <span className="rd-hero-stat-label">Delivered</span>
            </div>
            <div className="rd-hero-stat-divider" />
            <div className="rd-hero-stat">
              <span className="rd-hero-stat-num">{stats.active}</span>
              <span className="rd-hero-stat-label">Active</span>
            </div>
            <div className="rd-hero-stat-divider" />
            <div className="rd-hero-stat">
              <span className="rd-hero-stat-num">Rs {stats.earnings.toLocaleString()}</span>
              <span className="rd-hero-stat-label">Earnings</span>
            </div>
            <div className="rd-hero-stat-divider" />
            <div className="rd-hero-stat">
              <span className="rd-hero-stat-num">{stats.total}</span>
              <span className="rd-hero-stat-label">Total Orders</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="rd-alert">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* ══ DATE FILTER ══════════════════════════════════════ */}
        <div className="rd-filter-card">
          <span className="rd-filter-title">Filter by Period</span>
          <div className="rd-filter-tabs">
            {DATE_TABS.map(({ value, label, icon }) => (
              <button
                key={value}
                className={`rd-tab ${dateFilter === value ? 'rd-tab-active' : ''}`}
                onClick={() => setDateFilter(value)}
              >
                <span>{icon}</span>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ══ CHART CARD ═══════════════════════════════════════ */}
        <div className="rd-card">
          <div className="rd-card-header">
            <h2 className="rd-card-title">📊 Performance Overview</h2>
            <span className="rd-total-pill">{stats.total} orders</span>
          </div>
          <MiniBarChart
            delivered={stats.delivered}
            active={stats.active}
            cancelled={stats.cancelled}
            pending={stats.pending}
          />
        </div>

        {/* ══ STATS GRID ═══════════════════════════════════════ */}
        <div className="rd-stats-grid">
          <div className="rd-stat-card" style={{ background: '#f0fff4' }}>
            <div className="rd-stat-icon-wrap" style={{ background: '#d8f3dc' }}>🚴</div>
            <div className="rd-stat-num" style={{ color: '#2d6a4f' }}>{stats.active}</div>
            <div className="rd-stat-label">Active</div>
            <div className="rd-stat-bar-track" style={{ background: '#d8f3dc' }}>
              <div className="rd-stat-bar-fill" style={{ background: '#2d6a4f', width: `${stats.total ? (stats.active/stats.total)*100 : 0}%` }} />
            </div>
          </div>

          <div className="rd-stat-card" style={{ background: '#f0fdf4' }}>
            <div className="rd-stat-icon-wrap" style={{ background: '#dcfce7' }}>✅</div>
            <div className="rd-stat-num" style={{ color: '#16a34a' }}>{stats.delivered}</div>
            <div className="rd-stat-label">Delivered</div>
            <div className="rd-stat-bar-track" style={{ background: '#bbf7d0' }}>
              <div className="rd-stat-bar-fill" style={{ background: '#16a34a', width: `${stats.total ? (stats.delivered/stats.total)*100 : 0}%` }} />
            </div>
          </div>

          <div className="rd-stat-card" style={{ background: '#fff5f5' }}>
            <div className="rd-stat-icon-wrap" style={{ background: '#fee2e2' }}>❌</div>
            <div className="rd-stat-num" style={{ color: '#dc2626' }}>{stats.cancelled}</div>
            <div className="rd-stat-label">Cancelled</div>
            <div className="rd-stat-bar-track" style={{ background: '#fecaca' }}>
              <div className="rd-stat-bar-fill" style={{ background: '#dc2626', width: `${stats.total ? (stats.cancelled/stats.total)*100 : 0}%` }} />
            </div>
          </div>

          <div className="rd-stat-card" style={{ background: '#fffbeb' }}>
            <div className="rd-stat-icon-wrap" style={{ background: '#fef3c7' }}>⏳</div>
            <div className="rd-stat-num" style={{ color: '#d97706' }}>{stats.pending}</div>
            <div className="rd-stat-label">Available</div>
            <div className="rd-stat-bar-track" style={{ background: '#fde68a' }}>
              <div className="rd-stat-bar-fill" style={{ background: '#d97706', width: `${stats.total ? (stats.pending/stats.total)*100 : 0}%` }} />
            </div>
          </div>
        </div>

        {/* ══ EARNINGS CARD ════════════════════════════════════ */}
        <div className="rd-earnings-card">
          <div className="rd-earnings-bubble" />
          <div className="rd-earnings-top">
            <div>
              <span className="rd-earnings-label">Total Earnings</span>
              <div className="rd-earnings-value">Rs {stats.earnings.toLocaleString()}</div>
              <span className="rd-earnings-sub">Rs {DELIVERY_FEE} × {stats.delivered} deliveries</span>
            </div>
            <div className="rd-earnings-badge-wrap">💰</div>
          </div>
          <div className="rd-earnings-bar-track">
            <div className="rd-earnings-bar-fill" style={{ width: `${earningsPct}%` }} />
          </div>
          <div className="rd-earnings-footer">
            <span>Potential: Rs {stats.potential.toLocaleString()}</span>
            <span className="rd-earnings-pct">{Math.round(earningsPct)}% earned</span>
          </div>
        </div>

        {/* ══ RECENT ORDERS ════════════════════════════════════ */}
        <div className="rd-card">
          <div className="rd-card-header">
            <div className="rd-orders-title-wrap">
              <h2 className="rd-card-title">📋 Recent Orders</h2>
              <span className="rd-count-badge">{dashboardOrders.length}</span>
            </div>
            <button className="rd-see-all-btn" onClick={() => navigate('/rider/orders')}>
              See All →
            </button>
          </div>

          {dashboardOrders.length === 0 ? (
            <div className="rd-empty">
              <div className="rd-empty-icon">🛵</div>
              <h3 className="rd-empty-title">No orders found</h3>
              <p className="rd-empty-sub">No orders for the selected period.</p>
            </div>
          ) : (
            <div className="rd-orders-list">
              {dashboardOrders.slice(0, 5).map((order) => {
                const status  = String(order?.status || '').toLowerCase() || 'pending';
                const cfg     = getStatusCfg(status);
                const orderNo = order?.order_number || `#${order?.id}`;
                const address = order?.delivery_address || order?.deliveryAddress || '—';
                const phone   = order?.delivery_phone || order?.deliveryPhone || '—';
                const total   = order?.total_amount || order?.totalAmount || 0;
                const dt      = getFilterDate(order);
                const timeStr = dt ? dt.toLocaleDateString('en-PK', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '';

                return (
                  <div key={order.id || order._id} className="rd-order-card">
                    <div className="rd-order-accent" style={{ backgroundColor: cfg.color }} />
                    <div className="rd-order-body">
                      <div className="rd-order-top">
                        <div>
                          <span className="rd-order-num">{orderNo}</span>
                          {timeStr && <span className="rd-order-time">{timeStr}</span>}
                        </div>
                        <span className="rd-status-badge" style={{ background: cfg.bg, color: cfg.color }}>
                          {cfg.icon} {status.toUpperCase()}
                        </span>
                      </div>

                      <div className="rd-order-info">
                        <div className="rd-info-row"><span>📍</span><span className="rd-info-text">{address}</span></div>
                        <div className="rd-info-row"><span>📞</span><span className="rd-info-text">{phone}</span></div>
                      </div>

                      <div className="rd-order-footer">
                        <div>
                          <span className="rd-amount-label">Amount</span>
                          <div className="rd-amount-value">Rs {total}</div>
                        </div>
                        <button className="rd-view-btn" onClick={() => navigate('/rider/orders')}>
                          View Details →
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ══ QUICK ACTIONS ════════════════════════════════════ */}
        <div className="rd-card">
          <h2 className="rd-card-title">⚡ Quick Actions</h2>
          <div className="rd-actions-grid">
            <button className="rd-action-btn" onClick={() => navigate('/rider/orders')}>
              <span className="rd-action-icon">📦</span>
              <span className="rd-action-label">All Orders</span>
            </button>
            <button className="rd-action-btn" onClick={() => navigate('/rider/delivered')}>
              <span className="rd-action-icon">✅</span>
              <span className="rd-action-label">Delivered</span>
            </button>
            <button className="rd-action-btn" onClick={() => navigate('/rider/profile')}>
              <span className="rd-action-icon">👤</span>
              <span className="rd-action-label">My Profile</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}